import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import LinearProgress from '@mui/material/LinearProgress';
import { Pagination } from '../../../../model/pagination';
import { IPurchasePagedResponse } from '../../../../model/scp/purchase/response/purchase-paged-response';
import { IPurchasePageRequest } from '../../../../model/scp/purchase/request/purchase-paged-request';
import PurchaseAppService from '../../../../application/platform/scp/purchase/purchase-app-service';
import { Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { DefaultDialogProps } from '../../../../components/default-dialog';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import PreviewIcon from '@mui/icons-material/Preview';
import SettingsIcon from '@mui/icons-material/Settings';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useBaseLayout } from '../../../../components/context/use-base-layout';
import { dataGridLocaleConfig } from '../../../../config/data-grid-locale';

export interface IPurchaseDataTableProps {
    parameters: IPurchasePageRequest;
    searchTrigger?: number;
};
interface IPurchaseDataTableMenuContextProp {
    purchase: IPurchasePagedResponse,
    loadData: (parameters: IPurchasePageRequest) => void
};
function MenuPopupState({ props }: { props: IPurchaseDataTableMenuContextProp }) {
    const { showDialog, showMessage, showLoading } = useBaseLayout();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const gotToDetails = () => {
        handleClose();
        window.open(props.purchase.url, '_blank');
    }

    return (
        <div>
            <IconButton
                color="primary"
                // variant="contained"
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}>
                <SettingsIcon />
            </IconButton>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem onClick={gotToDetails}>
                    <ListItemIcon>
                        <PreviewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Acessar</ListItemText>
                </MenuItem>
                <MenuItem onClick={async () => {
                    handleClose();
                    navigate(`/compras-licitacoes/publicar/${props.purchase.id}`);
                }}>
                    <ListItemIcon>
                        <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    showDialog({
                        title: 'Pergunta', message: 'Deseja realmente excluir a compra/licitação? Caso deseje excluir preencha a justificativa abaixo:', onYesClick: (justification: string) => {
                            if (justification !== undefined && justification.length > 0) {
                                (async () => {
                                    showLoading({ open: true });
                                    await PurchaseAppService.deletePurchase(justification, props.purchase.id);
                                    props.loadData({} as IPurchasePageRequest);
                                    showLoading({ open: false });
                                    showMessage({ message: 'Compra ou licitação excluída com sucesso', type: 'success', open: true });
                                })();
                            } else {
                                showMessage({ message: 'Informe a justificativa para excluir a compra ou licitação', type: 'warning', open: true });
                            }
                        }, inputProps: {
                            rows: 4
                        }
                    } as DefaultDialogProps);

                }}>
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Excluir</ListItemText>
                </MenuItem>
            </Menu>
        </div>
    );
}

export default function PurchaseDataTable({ props }: { props: IPurchaseDataTableProps }) {
    const [response, setResponse] = useState<Pagination<IPurchasePagedResponse>>(new Pagination<IPurchasePagedResponse>);
    const [purchaseDataTableProps, setPurchaseDataTableProps] = useState<IPurchaseDataTableProps>(props);
    const [loadMoreData, setLoadMoreData] = useState(true);


    const loadData = (parameters: IPurchasePageRequest) => {
        (async () => {
            if (parameters !== undefined) {
                setLoadMoreData(true);
                const data = await PurchaseAppService.listPurchase(parameters);
                setResponse(data);
                setLoadMoreData(false);
            }
        })();
    }

    const columns: GridColDef<IPurchasePagedResponse>[] = [
        { field: 'id', headerName: 'Id', width: 90, hide: true },
        {
            field: 'number',
            headerName: 'Nº Compra',
            flex: 1,
            minWidth: 130
        },
        {
            field: 'numberProcess',
            headerName: 'Nº Processo',
            flex: 1,
            minWidth: 140
        }, {
            field: 'year',
            headerName: 'Exercício',
            minWidth: 100
        },
        {
            field: 'title',
            headerName: 'Título',
            flex: 1,
            minWidth: 160
        },
        {
            field: 'object',
            headerName: 'Objeto',
            flex: 1,
            minWidth: 160
        },
        {
            field: 'modality',
            headerName: 'Modalidade',
            flex: 1,
            minWidth: 150
        },
        {
            field: 'disputeMode',
            headerName: 'ModoDisputa',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                let chip = <Chip label={params.row.disputeMode} color="primary" />
                if (params.row.disputeModeId === 1) {
                    chip = <Chip label={params.row.disputeMode} color="primary" />
                } else if (params.row.disputeModeId === 2) {
                    chip = <Chip label={params.row.disputeMode} color="secondary" />
                } else if (params.row.disputeModeId === 3) {
                    chip = <Chip label={params.row.disputeMode} color="warning" />
                } else if (params.row.disputeModeId === 4) {
                    chip = <Chip label={params.row.disputeMode} color="error" />
                } else if (params.row.disputeModeId === 5) {
                    chip = <Chip label={params.row.disputeMode} color="success" />
                } else if (params.row.disputeModeId === 6) {
                    chip = <Chip label={params.row.disputeMode} color="default" />
                }
                return chip;
            }
        },
        {
            field: 'situation',
            headerName: 'Situação',
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                let chip = <Chip label={params.row.situation} color="primary" />
                if (params.row.situationId === 1) {
                    chip = <Chip label={params.row.situation} color="primary" />
                } else if (params.row.situationId === 2) {
                    chip = <Chip label={params.row.situation} color="secondary" />
                } else if (params.row.situationId === 3) {
                    chip = <Chip label={params.row.situation} color="warning" />
                }
                return chip;
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                return (
                    <MenuPopupState props={{ purchase: params.row, loadData: loadData }}></MenuPopupState>

                );
            }
        }
    ];

    useEffect(() => {
        loadData(props.parameters);
        setPurchaseDataTableProps(props);
    }, [props.parameters.page, props.searchTrigger]); // Re-executa quando página ou searchTrigger mudarem

    return (<React.Fragment>
        <DataGrid
            rows={response.items}
            columns={columns}
            page={purchaseDataTableProps?.parameters.page}
            pageSize={response.pageSize}
            rowCount={response.totalRecords}
            disableSelectionOnClick
            onPageChange={(newPage) => {
                const newParameters = { ...purchaseDataTableProps.parameters, page: newPage };
                setPurchaseDataTableProps({ ...purchaseDataTableProps, parameters: newParameters });
                loadData(newParameters);
            }}
            paginationMode="server"
            components={{
                LoadingOverlay: LinearProgress,
            }}
            loading={loadMoreData}
            rowsPerPageOptions={[response.pageSize]}
            experimentalFeatures={{ newEditingApi: true }}
            localeText={dataGridLocaleConfig}
        />
    </React.Fragment>
    )
}